import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const NODES = [
  { id: "01", label: "INCIDENCIA DETECTADA" },
  { id: "02", label: "RECIBIDA POR APREA" },
  { id: "03", label: "TÉCNICO ASIGNADO" },
  { id: "04", label: "CUENTA ATRÁS ACTIVA" },
];

function useTicker() {
  const [secs, setSecs] = useState(24 * 3600);
  useEffect(() => {
    const i = setInterval(() => setSecs((s) => (s <= 0 ? 24 * 3600 : s - 1)), 1000);
    return () => clearInterval(i);
  }, []);
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function IncidentSystem() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [active, setActive] = useState(0);
  const time = useTicker();

  useEffect(() => {
    if (!inView) return;
    const i = setInterval(() => setActive((a) => (a + 1) % NODES.length), 1800);
    return () => clearInterval(i);
  }, [inView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative border border-silver/15 bg-panel/60 backdrop-blur-xl p-7 md:p-9"
      data-testid="incident-system"
    >
      <span className="absolute -top-px -left-px w-5 h-5 border-t border-l border-silver/50" />
      <span className="absolute -bottom-px -right-px w-5 h-5 border-b border-r border-silver/50" />

      <div className="flex items-center justify-between mb-8">
        <span className="font-mono text-[10px] tracking-[0.3em] text-steel">SISTEMA DE GESTIÓN — APREA</span>
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-glow">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-glow" />
          OPERATIVO
        </span>
      </div>

      <div className="space-y-0">
        {NODES.map((node, i) => (
          <div key={node.id} className="relative">
            {i < NODES.length - 1 && (
              <span className="absolute left-[5px] top-7 bottom-0 w-px bg-silver/10">
                <motion.span
                  className="block w-px bg-glow/70 origin-top"
                  animate={{ scaleY: active > i ? 1 : 0, height: "100%" }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </span>
            )}
            <div className="flex items-center gap-5 py-4">
              <span
                className={`relative z-10 h-[11px] w-[11px] rotate-45 border transition-colors duration-500 ${
                  active === i
                    ? "bg-glow border-glow"
                    : active > i
                      ? "bg-silver/70 border-silver/70"
                      : "bg-transparent border-steel"
                }`}
              />
              <span className="font-mono text-[10px] text-steel w-6">{node.id}</span>
              <span
                className={`font-mono text-xs md:text-sm tracking-[0.18em] transition-colors duration-500 ${
                  active === i ? "text-offwhite" : "text-mist/70"
                }`}
              >
                {node.label}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 pt-7 border-t border-silver/10 flex items-end justify-between">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-steel block mb-2">
            COMPROMISO MÁXIMO — HORAS HÁBILES
          </span>
          <span className="font-mono text-4xl md:text-5xl text-metal tabular-nums whitespace-nowrap" data-testid="hero-countdown">
            {time}
          </span>
        </div>
        <span className="font-mono text-[10px] tracking-[0.2em] text-steel text-right leading-relaxed">
          08:00—20:00
          <br />
          DOM·FEST EXCL.
        </span>
      </div>
    </motion.div>
  );
}
